import React, { useState, useEffect, useRef } from 'react';
import { Link, navigate } from 'gatsby';
import { Helmet } from 'react-helmet';
import styled, { createGlobalStyle } from 'styled-components';

// `path` stays on this site; `url` leaves it for a separately hosted subdomain
const SUBDOMAINS = [
  { name: 'finance', url: 'https://finance.ionwyn.com/' },
  { name: 'letters', url: 'https://letters.ionwyn.com' },
  { name: 'music', path: '/music/' },
  { name: 'photos', path: '/photos/' },
  { name: 'tech', path: '/tech/' },
];

const SelectorGlobalStyle = createGlobalStyle`
  :root {
    --color-bg: #f3f2f2;
    --color-text: #201e1d;
    --color-accent: rgb(232, 90, 79);
    --color-neutral-300: #d7d3d3;
    --font-heading: 'Archivo', system-ui, sans-serif;
    --font-heading-weight: 800;
    --font-body: 'Archivo', system-ui, sans-serif;
    --space-4: 16px;
    --space-8: 32px;
  }

  *, *::before, *::after { box-sizing: border-box; }

  html,
  body {
    margin: 0;
    height: 100%;
    background: var(--color-bg);
    overscroll-behavior: none;
  }

  :focus { outline: none; }
  :focus-visible { outline: 2px solid var(--color-accent); outline-offset: 2px; }
  ::selection { background: color-mix(in srgb, var(--color-accent) 30%, transparent); }
`;

const StyledMain = styled.main`
  position: relative;
  min-height: 100vh;
  min-height: 100dvh;
  display: grid;
  /* Vertical swipes pick a subdomain, so don't let them scroll the page; pinch zoom still works */
  touch-action: pinch-zoom;
  font-family: var(--font-body);
  color: var(--color-text);
  background: var(--color-bg);
`;

const StyledSelector = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  overflow: hidden;
  font-family: var(--font-heading);
  font-weight: var(--font-heading-weight);
  font-size: clamp(32px, 7vw, 120px);
  line-height: 1.1;
  letter-spacing: -0.03em;

  /* Full-height hover zone for the list; it never moves, so the pointer can't lose it */
  .column {
    align-self: stretch;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    min-width: 0;
  }

  .window {
    position: relative;
    height: 1.1em;
  }

  /* The list is absolutely positioned, so this hidden copy gives the column the widest word's width */
  .sizer {
    display: block;
    height: 0;
    overflow: hidden;
    visibility: hidden;
    white-space: nowrap;
  }

  nav {
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    transition: transform 420ms cubic-bezier(0.2, 0.7, 0.2, 1);
  }

  a {
    display: block;
    height: 1.1em;
    color: var(--color-neutral-300);
    text-decoration: none;
    transition: color 200ms;

    &[aria-current='true'] {
      color: var(--color-accent);
    }
  }

  .domain {
    white-space: nowrap;
  }

  /* On narrow screens the word column can't fit beside a centered domain, so center the pair
     as a whole inside the side gutters and size the type to fill the width */
  @media (max-width: 700px) {
    grid-template-columns: auto auto;
    justify-content: center;
    padding: 0 var(--space-4);
    /* widest word + ".ionwyn.com" together measure about 9.6em at this weight */
    font-size: calc((100vw - 2 * var(--space-4)) / 9.9);

    .spacer {
      display: none;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    nav,
    a {
      transition: none;
    }
  }
`;

const StyledHint = styled.p`
  position: absolute;
  left: var(--space-8);
  bottom: var(--space-4);
  margin: 0;
  font-size: 13px;

  @media (max-width: 700px) {
    left: var(--space-4);
  }

  .touch {
    display: none;
  }

  @media (hover: none) and (pointer: coarse) {
    .keys {
      display: none;
    }
    .touch {
      display: inline;
    }
  }
`;

const SWIPE_THRESHOLD = 30;

const IndexPage = () => {
  const [active, setActive] = useState(0);
  // While the pointer is over the list, hold it at this index so words don't slide out from
  // under the cursor; it settles on the active word once the pointer leaves.
  const [pinned, setPinned] = useState(null);
  const activeRef = useRef(active);
  activeRef.current = active;
  const lastPointer = useRef(null);

  const onColumnMouseMove = e => {
    // Browsers re-fire mouse events when content moves under a still cursor; only real moves count
    const last = lastPointer.current;
    if (last && last.x === e.clientX && last.y === e.clientY) {
      return;
    }
    lastPointer.current = { x: e.clientX, y: e.clientY };

    setPinned(p => (p === null ? activeRef.current : p));
    const link = e.target.closest('a[data-index]');
    if (link) {
      setActive(Number(link.dataset.index));
    }
  };

  const onColumnMouseLeave = () => {
    lastPointer.current = null;
    setPinned(null);
  };

  useEffect(() => {
    const n = SUBDOMAINS.length;
    const step = delta => {
      setPinned(null);
      setActive(a => (a + delta + n) % n);
    };

    const onKey = e => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        step(1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        step(-1);
      } else if (e.key === 'Enter' && !(e.target instanceof HTMLAnchorElement)) {
        const { path, url } = SUBDOMAINS[activeRef.current];
        if (url) {
          window.location.href = url;
        } else {
          navigate(path);
        }
      }
    };

    // Swiping up moves to the next word, like dragging the list up
    let touchStartY = null;
    const onTouchStart = e => {
      touchStartY = e.touches.length === 1 ? e.touches[0].clientY : null;
    };
    const onTouchEnd = e => {
      if (touchStartY === null) {
        return;
      }
      const dy = e.changedTouches[0].clientY - touchStartY;
      touchStartY = null;
      if (Math.abs(dy) >= SWIPE_THRESHOLD) {
        step(dy < 0 ? 1 : -1);
      }
    };

    window.addEventListener('keydown', onKey);
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchend', onTouchEnd);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, []);

  return (
    <>
      <Helmet title="Ionwyn">
        <html lang="en" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Archivo:wght@400;800&display=swap"
        />
      </Helmet>
      <SelectorGlobalStyle />

      <StyledMain>
        <StyledSelector>
          <div className="column" onMouseMove={onColumnMouseMove} onMouseLeave={onColumnMouseLeave}>
            <div className="window">
              <span className="sizer" aria-hidden="true">
                {SUBDOMAINS.map(({ name }) => (
                  <span key={name} style={{ display: 'block' }}>
                    {name}
                  </span>
                ))}
              </span>
              <nav
                aria-label="Subdomains"
                style={{ transform: `translateY(${-1.1 * (pinned ?? active)}em)` }}>
                {SUBDOMAINS.map(({ name, path, url }, i) => {
                  const props = {
                    'data-index': i,
                    'aria-current': i === active ? 'true' : undefined,
                    onFocus: () => setActive(i),
                  };
                  return url ? (
                    <a key={name} href={url} {...props}>
                      {name}
                    </a>
                  ) : (
                    <Link key={name} to={path} {...props}>
                      {name}
                    </Link>
                  );
                })}
              </nav>
            </div>
          </div>
          <div className="domain">.ionwyn.com</div>
          <div className="spacer" />
        </StyledSelector>

        <StyledHint>
          <span className="keys">↑ ↓ to choose · Enter to go</span>
          <span className="touch">Swipe to choose · Tap to go</span>
        </StyledHint>
      </StyledMain>
    </>
  );
};

export default IndexPage;
